import { Link } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { PageIntro, PageBadgeBand, Section } from "@/components/site/PageShell";
import { SITE } from "@/constants/testIds";
import { useMeta } from "@/hooks/useMeta";

const SECTIONS = [
  {
    id: "who-we-are",
    heading: "1. Who we are",
    body: (
      <p>
        YeboNadi is a product of Simple Concepts LLC (&ldquo;we&rdquo;,
        &ldquo;us&rdquo;, &ldquo;YeboNadi&rdquo;), with its mailing address at
        2820 S Alma School Rd, Suite 18, PMB 2034, Chandler, AZ 85286.
        &ldquo;You&rdquo; means the individual person using the app or
        website.
      </p>
    ),
  },
  {
    id: "acceptance",
    heading: "2. Acceptance of these Terms",
    body: (
      <p>
        By installing or using the YeboNadi app, or by using this website, you
        agree to these Terms. If you do not agree, do not use YeboNadi.
      </p>
    ),
  },
  {
    id: "what-the-service-is",
    heading: "3. What the service is",
    body: (
      <>
        <p>
          YeboNadi is a secure evidence-recording app. It captures audio,
          timestamp, and GPS location; encrypts and hash-seals that record at
          the moment of capture; and backs it up to encrypted cloud storage.
          It is intended for the user&apos;s own protection, to preserve a
          verifiable record of interactions the user is present in.
        </p>
        <p>
          It is not a dictation app or a general surveillance tool. Use for
          those purposes is prohibited under Section 5 and the{" "}
          <Link
            to="/undertaking"
            className="text-[color:var(--yn-primary)] hover:underline"
          >
            Undertaking
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    heading: "4. Eligibility",
    body: (
      <p>
        You must be at least 18 years old and legally able to enter into a
        binding agreement in your jurisdiction to use YeboNadi. By using the
        app you represent that you meet these requirements.
      </p>
    ),
  },
  {
    id: "undertaking-incorporation",
    heading: "5. The Undertaking, incorporated by reference",
    body: (
      <>
        <p>
          As a condition of use, every YeboNadi user must accept and sign the
          YeboNadi Undertaking inside the app before making their first sealed
          recording. The Undertaking is incorporated into these Terms by
          reference and forms part of your binding agreement with Simple
          Concepts LLC.
        </p>
        <p>
          The Undertaking is short, plainly written, and available for review
          before you install the app on the{" "}
          <Link
            to="/undertaking"
            className="text-[color:var(--yn-primary)] hover:underline"
          >
            Undertaking page
          </Link>
          . If the Undertaking and these Terms ever conflict on the subject of
          acceptable use, the stricter provision applies.
        </p>
        <p>
          Breach of the Undertaking is a material breach of these Terms and is
          independently grounds for immediate suspension or termination under
          Section 12.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    heading: "6. Acceptable use",
    body: (
      <>
        <p>
          You agree to use YeboNadi only for lawful purposes. Specifically,
          you agree not to:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Record any person in a way that violates the recording-consent
            laws that apply where you are recording.
          </li>
          <li>
            Use YeboNadi or any recording captured with it to blackmail,
            extort, harass, intimidate, or coerce any person.
          </li>
          <li>
            Use YeboNadi to conduct unlawful surveillance, including
            surveillance of children, intimate partners, employees, or any
            other person in a manner prohibited by law.
          </li>
          <li>
            Attempt to bypass, alter, or interfere with the technical
            protections of the app (encryption, fingerprinting, timestamps,
            GPS embedding, audit logging, or the retrieval-release process).
          </li>
          <li>
            Resell, sublicense, or commercially redistribute the app or any
            sealed recording released to you (or to a third party on your
            behalf) through the retrieval process without our written
            permission.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "your-content",
    heading: "7. Your recordings",
    body: (
      <>
        <p>
          Your recordings are yours. We do not claim ownership of them. We
          store them, in encrypted form, so that the sealed record survives
          the phone that captured it. Access to a sealed recording, for
          you or for anyone else, always goes through the
          retrieval-release process described in the Privacy policy: a
          legal order or signed release along with your encryption/decryption
          key.
        </p>
        <p>
          You are responsible for the contents of your recordings and for
          ensuring that you had the legal right to capture them.
        </p>
      </>
    ),
  },
  {
    id: "subscriptions",
    heading: "8. Subscriptions and billing",
    body: (
      <>
        <p>
          Paid plans are billed monthly in advance in US dollars. You can
          cancel from inside the app at any time; cancellation ends future
          billing but does not refund the current period. If a payment fails,
          we will hold your encrypted recordings during a grace period; if the
          account is not brought current, recordings age out on the retention
          schedule described in{" "}
          <Link
            to="/privacy"
            className="text-[color:var(--yn-primary)] hover:underline"
          >
            Privacy
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    id: "no-legal-advice",
    heading: "9. No legal advice; admissibility disclaimer",
    body: (
      <p>
        YeboNadi is a tool, not a lawyer. Nothing on this website or inside
        the app is legal advice. Whether a specific recording is admissible in
        a specific proceeding depends on the law of the applicable
        jurisdiction and the facts of the case. If you plan to use a YeboNadi
        recording as evidence, consult a licensed attorney.
      </p>
    ),
  },
  {
    id: "warranty-disclaimer",
    heading: "10. Warranty disclaimer",
    body: (
      <p>
        YeboNadi is provided &ldquo;as is.&rdquo; To the fullest extent
        permitted by law, we disclaim all warranties, express or implied,
        including merchantability, fitness for a particular purpose, and
        non-infringement. We do not warrant that the service will be
        uninterrupted or error-free.
      </p>
    ),
  },
  {
    id: "liability",
    heading: "11. Limitation of liability",
    body: (
      <p>
        To the fullest extent permitted by law, Simple Concepts LLC will not
        be liable for indirect, incidental, special, consequential, or
        punitive damages, or any loss of profits or revenues, whether incurred
        directly or indirectly, or any loss of data, use, goodwill, or other
        intangible losses. Our total liability arising out of or relating to
        these Terms or the service will not exceed the amount you paid us in
        the 12 months preceding the event giving rise to the claim.
      </p>
    ),
  },
  {
    id: "termination",
    heading: "12. Suspension and termination",
    body: (
      <p>
        We may suspend or terminate your account for material breach of these
        Terms, breach of the Undertaking, or as required by law. You may stop
        using the service at any time and cancel your subscription from
        inside the app.
      </p>
    ),
  },
  {
    id: "changes",
    heading: "13. Changes to these Terms",
    body: (
      <p>
        We may update these Terms from time to time. If we make material
        changes, we will notify you inside the app and, where required, by
        email. Continuing to use the service after a change means you accept
        the updated Terms.
      </p>
    ),
  },
  {
    id: "governing-law",
    heading: "14. Governing law",
    body: (
      <p>
        These Terms are governed by the laws of the State of Arizona, USA,
        without regard to its conflicts-of-law rules. Any dispute arising out
        of or relating to these Terms will be brought in the state or federal
        courts located in Maricopa County, Arizona.
      </p>
    ),
  },
  {
    id: "contact",
    heading: "15. Contact",
    body: (
      <p>
        Simple Concepts LLC, 2820 S Alma School Rd, Suite 18, PMB 2034,
        Chandler, AZ 85286.{" "}
        <a
          href="mailto:info@concepts-simplified.com"
          className="text-[color:var(--yn-primary)] hover:underline"
        >
          info@concepts-simplified.com
        </a>{" · "}
        <a
          href="tel:+18324231808"
          className="text-[color:var(--yn-primary)] hover:underline"
        >
          +1 (832) 423-1808
        </a>
      </p>
    ),
  },
];

export default function Terms() {
  useMeta({
    title: "Terms of Service, YeboNadi",
    description:
      "Plain-English Terms of Service for YeboNadi, the evidence recording app. Includes acceptable-use, the Undertaking incorporation, subscription terms, and governing law.",
    path: "/terms",
  });
  return (
    <div data-testid={SITE.termsPage}>
      <PageIntro
        kicker="Terms"
        title="Terms of Service."
        lede="Plain-English rules of the road for using YeboNadi. If it ever conflicts with the in-app agreement you accepted, the in-app agreement controls."
      >
        <p className="text-[color:var(--yn-muted)] text-sm">
          Effective date: 30 March 2026
        </p>
      </PageIntro>

      {/* Legal-defer callout, the authoritative T&Cs live in the app */}
      <Section className="!pt-0 !pb-0">
        <div
          className="max-w-3xl rounded-2xl border border-[color:var(--yn-primary)]/30 bg-[color:var(--yn-primary)]/5 p-6 sm:p-8"
          data-testid="site-terms-defer-callout"
        >
          <div className="flex items-start gap-3">
            <Smartphone
              size={22}
              className="mt-1 shrink-0"
              style={{ color: "var(--yn-primary)" }}
            />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
                Authoritative version, inside the app
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--yn-ink)]">
                The current, binding Terms and Conditions live inside the
                YeboNadi app, they are what you read and accept before your
                first recording. This page is a plain-English reference
                summary for the public web. If anything here differs from the
                in-app agreement, the in-app agreement is what applies.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="max-w-3xl">
          {/* On-page TOC */}
          <nav aria-label="On this page" className="mb-10">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-muted)] mb-3">
              On this page
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-12">
            {SECTIONS.map((s) => (
              <article key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="font-heading text-2xl font-bold text-[color:var(--yn-ink)] tracking-tight">
                  {s.heading}
                </h2>
                <div className="mt-4 space-y-4 text-[16px] leading-[1.75] text-[color:var(--yn-muted)]">
                  {s.body}
                </div>
              </article>
            ))}
          </div>
        </div>
      </Section>

      <PageBadgeBand heading="You've read the terms. Ready when you are." />
    </div>
  );
}
